import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import Button from "../../components/button/Button";
import { BasicModal } from "../../components/modal/ModalUI";
import { basicModalShowHide, setTitle } from "../../features/modalSlice";
import {
  fetchTopups,
  resetTopupFields,
  setTopupFields,
  updateTopupStatus,
} from "../../features/topupSlice";
import { can } from "../../utils/permission.util";
import TopupModal from "./TopupModal";

const getNormalizedTopup = (item = {}) => ({
  id: item.id ?? "",
  name: item.name ?? "",
  description: item.description ?? "",
  items: item.items ?? item.no_of_items ?? item.noOfItems ?? "",
  teams: item.teams ?? item.no_of_teams ?? item.noOfTeams ?? "",
  price: item.price ?? "",
});

const Topups = () => {
  const dispatch = useDispatch();
  const { listData, totalCount } = useSelector((state) => state.topup);

  const [searchText, setSearchText] = useState("");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);

  const canCreateTopup = can("/topups", "create") || can("/subscription", "create");
  const canDeleteTopup = can("/topups", "delete") || can("/subscription", "delete");

  const rows = useMemo(() => (Array.isArray(listData) ? listData : []), [listData]);

  useEffect(() => {
    dispatch(fetchTopups({ offset, limit, searchText }));
  }, [dispatch, limit, offset, searchText]);

  const openAddModal = () => {
    dispatch(resetTopupFields());
    dispatch(setTitle("Add New"));
    dispatch(basicModalShowHide());
  };

  const closeModal = () => {
    dispatch(basicModalShowHide());
    dispatch(resetTopupFields());
  };

  const editTopup = (item) => {
    dispatch(setTopupFields(getNormalizedTopup(item)));
    dispatch(setTitle("Edit"));
    dispatch(basicModalShowHide());
  };

  const itemStatusChange = (id, status) => {
    dispatch(updateTopupStatus({ id, status }));
  };

  const truncateDescription = (description) => {
    if (!description) return "";
    return description.length > 100 ? `${description.substring(0, 100)}...` : description;
  };

  const nextPage = () => {
    setOffset((prevOffset) => prevOffset + limit);
  };

  const prevPage = () => {
    setOffset((prevOffset) => Math.max(0, prevOffset - limit));
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setOffset(0);
  };

  return (
    <div id="main" className="main">
      <Breadcrumb page="Add-ons" />

      <section className="section">
        <div className="row mb-3">
          <div className="col-sm-6 col-md-12 col-lg-12 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2 item_list">
              <h6 className="card-title m-0 p-0">Add-ons</h6>
              <input
                className="form-control"
                placeholder="search"
                type="text"
                name="search"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setOffset(0);
                }}
              />
            </div>

            {canCreateTopup && (
              <Button
                className="btn btn-sm btn-outline-primary me-2"
                title="Add Topup"
                onClick={openAddModal}
              >
                <i className="fa-solid fa-plus" />
              </Button>
            )}
          </div>
        </div>

        <table className="table table-bordered">
          <thead>
            <tr>
              <th scope="col" className="text-center fs-7">
                Name
              </th>
              <th scope="col" className="text-center fs-7">
                Description
              </th>
              <th scope="col" className="text-center fs-7">
                No of Challenges
              </th>
              <th scope="col" className="text-center fs-7">
                No of Teams
              </th>
              <th scope="col" className="text-center fs-7">
                Price
              </th>
              {(canCreateTopup || canDeleteTopup) && (
                <th scope="col" className="text-center fs-7">
                  Action
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {rows.length
              ? rows.map((item, i) => (
                  <tr key={`topupList-${item?.id ?? i}`}>
                    <td className="text-center fs-7">{item?.name || "-"}</td>
                    <td className="text-center fs-7 align-middle">
                      <span title={item?.description || ""}>
                        {truncateDescription(item?.description)}
                      </span>
                    </td>
                    <td className="text-center fs-7 align-middle">
                      {item?.items ?? "-"}
                    </td>
                    <td className="text-center fs-7 align-middle">
                      {item?.teams ?? "-"}
                    </td>
                    <td className="text-center fs-7 align-middle">
                      {item?.price === 0 || item?.price ? `$ ${item.price}` : "-"}
                    </td>

                    {(canCreateTopup || canDeleteTopup) && (
                      <td className="text-center fs-7 align-middle">
                        {canCreateTopup &&
                          (Number(item?.status) === 1 ? (
                            <Button
                              className="btn btn-outline-success btn-sm fs-7 me-2"
                              onClick={() => itemStatusChange(item.id, 0)}
                              title="Set Inactive"
                            >
                              <i className="fa-solid fa-cloud-arrow-up"></i>
                            </Button>
                          ) : (
                            <Button
                              className="btn btn-outline-danger btn-sm fs-7 me-2"
                              onClick={() => itemStatusChange(item.id, 1)}
                              title="Set Active"
                            >
                              <i className="fa-solid fa-cloud-arrow-up"></i>
                            </Button>
                          ))}

                        {canCreateTopup && (
                          <Button
                            className="btn btn-outline-success btn-sm fs-7 me-2"
                            title="Edit Topup"
                            onClick={() => editTopup(item)}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </Button>
                        )}

                        {canDeleteTopup && (
                          <Button
                            className="btn btn-outline-danger btn-sm fs-7 me-2"
                            onClick={() => itemStatusChange(item.id, 2)}
                            title="Delete Topup"
                          >
                            <i className="fa-regular fa-trash-can"></i>
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              : null}
          </tbody>
        </table>

        <div className="d-flex justify-content-end align-items-center mt-3">
          <div className="form-group me-3 d-flex align-items-center">
            <select
              className="form-select form-select-sm me-2"
              value={limit}
              onChange={handleLimitChange}
            >
              {[10, 20, 50, 100].map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </div>

          <Button
            className="btn btn-outline-primary btn-sm"
            onClick={prevPage}
            disabled={offset === 0}
          >
            Previous
          </Button>

          <Button
            className="btn btn-outline-primary btn-sm"
            onClick={nextPage}
            disabled={offset + limit >= totalCount || rows.length < limit}
          >
            Next
          </Button>
        </div>
      </section>

      <BasicModal>
        <TopupModal closeModal={closeModal} />
      </BasicModal>
    </div>
  );
};

export default Topups;
